import moment from 'moment';
import { getTone, toneAlpha, grey } from '../../../../theme/tones';
import 'moment/locale/fr';

/**
 * Les trois types de questionnaires apprenant, chacun avec son accent couleur :
 * l'onglet actif recolore les KPI, les cartes et le tiroir, pour qu'on sache
 * toujours d'un coup d'œil quel jeu de réponses on est en train de lire.
 */
export const QUESTIONNAIRE_TYPES = [
  {
    value: 'Pré-Formation',
    label: 'Pré-Formation',
    short: 'Pré',
    accent: getTone('amber').base,
    accentSoft: toneAlpha('amber', 0.12),
    gradient: `linear-gradient(135deg, ${getTone('amber').base} 0%, ${getTone('amber').color} 100%)`,
    hint: "Niveau de départ des apprenants avant l'entrée en formation.",
    scoreLabel: 'Niveau initial'
  },
  {
    value: 'Post-Formation',
    label: 'Post-Formation',
    short: 'Post',
    accent: getTone('violet').base,
    accentSoft: toneAlpha('violet', 0.12),
    gradient: `linear-gradient(135deg, ${getTone('violet').light} 0%, ${getTone('violet').color} 100%)`,
    hint: 'Acquis mesurés à la sortie de la formation.',
    scoreLabel: 'Niveau acquis'
  },
  {
    value: 'Satisfaction',
    label: 'Satisfaction',
    short: 'Satisfaction',
    accent: getTone('indigo').base,
    accentSoft: toneAlpha('indigo', 0.12),
    gradient: `linear-gradient(135deg, ${getTone('indigo').light} 0%, ${getTone('indigo').color} 100%)`,
    hint: 'Ressenti des apprenants à l’issue de la session.',
    scoreLabel: 'Satisfaction',
    // Seule la satisfaction se note sur 5 : sur Pré/Post le score est un taux de
    // bonnes réponses à un test de connaissances, pas une appréciation.
    rating: true
  },
  {
    // Pas une 4e phase commune : seules les formations qui en prévoient un
    // (certificat de décès, 248 et 254) ont un questionnaire de validation.
    // Noté comme un QCM, donc sans note sur 5. Doit rester APRÈS Satisfaction :
    // getTypeConfig() retombe sur l'index 2.
    value: 'Validation',
    label: 'Validation',
    short: 'Validation',
    accent: getTone('cyan').color,
    accentSoft: toneAlpha('cyan', 0.12),
    gradient: `linear-gradient(135deg, ${getTone('cyan').light} 0%, ${getTone('cyan').color} 100%)`,
    hint: 'Validation des acquis, pour les formations qui prévoient ce questionnaire.',
    scoreLabel: 'Score de validation'
  }
];

export const getTypeConfig = (value) =>
  QUESTIONNAIRE_TYPES.find((t) => t.value === value) || QUESTIONNAIRE_TYPES[2];

/**
 * Les formateurs ne sont pas un 4e type de questionnaire mais une autre
 * AUDIENCE : mêmes tables, répondant en `intervenant_id`, et deux modèles
 * seulement (présentiel / classe virtuelle) communs à toutes les formations.
 * D'où une page à part — mais présentée comme un onglet de plus, puisque c'est
 * là qu'on vient lire les réponses aux questionnaires.
 */
export const FORMATEUR_TAB = {
  value: 'Formateurs',
  // Le libellé porte l'audience à lui seul : les trois onglets apprenant sont
  // regroupés derrière une étiquette « Apprenant », celui-ci se suffit.
  label: 'Satisfaction des formateurs',
  short: 'Formateurs',
  accent: getTone('teal').color,
  accentSoft: toneAlpha('teal', 0.12),
  gradient: `linear-gradient(135deg, ${getTone('teal').light} 0%, ${getTone('teal').color} 100%)`,
  hint: 'Ressenti des formateurs à l’issue de leurs sessions.',
  scoreLabel: 'Satisfaction',
  rating: true,
  path: '/dashboard/questionnaire-formateurs'
};

/**
 * Le compte rendu Word n'est ni un type ni une audience : c'est un DOCUMENT,
 * monté au niveau de la session et couvrant les trois types à la fois. D'où sa
 * place à part dans la barre, derrière un second séparateur, et sa liste bâtie
 * sur `type=All` — se limiter au type de l'onglet d'où l'on vient masquerait
 * des sessions qui ont pourtant un compte rendu.
 */
export const COMPTE_RENDU_TAB = {
  value: 'ComptesRendus',
  label: 'Compte rendu',
  short: 'Compte rendu',
  accent: getTone('blue').text,
  accentSoft: toneAlpha('blue', 0.12),
  gradient: `linear-gradient(135deg, ${getTone('blue').base} 0%, ${getTone('blue').deep} 100%)`,
  hint: 'Compte rendu Word d’une session, tous questionnaires confondus.',
  path: '/dashboard/comptes-rendus'
};

/** Chemin de l'onglet des trois types apprenant, pour la navigation croisée. */
export const APPRENANT_PATH = '/dashboard/questionnaire-satisfaction';

/** Étiquette qui ouvre le groupe des trois onglets apprenant. */
export const APPRENANT_GROUP_LABEL = 'Apprenant';

/**
 * Le même tri sert aux deux niveaux de la vue cartes : les agrégats formation
 * exposent volontairement les mêmes champs qu'un groupe session.
 */
export const sortEntries = (entries, sortBy) => {
  const list = [...entries];
  const byDate = (g) => (g.start_date ? moment(g.start_date).valueOf() : 0);
  switch (sortBy) {
    case 'date_asc': return list.sort((a, b) => byDate(a) - byDate(b));
    case 'score_desc': return list.sort((a, b) => (b.average ?? -1) - (a.average ?? -1));
    case 'score_asc': return list.sort((a, b) => (a.average ?? 101) - (b.average ?? 101));
    case 'responses_desc': return list.sort((a, b) => b.responses - a.responses);
    case 'formation_asc': return list.sort((a, b) => (a.formation_name || '').localeCompare(b.formation_name || ''));
    default: return list.sort((a, b) => byDate(b) - byDate(a));
  }
};

/**
 * Regroupe les sessions par formation. La moyenne est pondérée par le nombre
 * de réponses : sinon une session à 2 réponses pèserait autant qu'une à 30.
 */
export const buildFormationCards = (groups) => {
  const map = new Map();

  groups.forEach((g) => {
    const key = String(g.formation_id);
    let entry = map.get(key);
    if (!entry) {
      entry = {
        formation_id: g.formation_id,
        formation_name: g.formation_name,
        program_number: g.program_number,
        sessions: 0,
        responses: 0,
        learners: 0,
        start_date: null,
        end_date: null,
        formats: [],
        professions: [],
        _scoreSum: 0,
        _scoreWeight: 0
      };
      map.set(key, entry);
    }

    entry.sessions += 1;
    entry.responses += g.responses || 0;
    entry.learners += g.learners || 0;
    if (!entry.formation_name && g.formation_name) entry.formation_name = g.formation_name;
    if (!entry.program_number && g.program_number) entry.program_number = g.program_number;

    if (g.start_date && (!entry.start_date || moment(g.start_date).isBefore(entry.start_date))) {
      entry.start_date = g.start_date;
    }
    const end = g.end_date || g.start_date;
    if (end && (!entry.end_date || moment(end).isAfter(entry.end_date))) {
      entry.end_date = end;
    }

    if (g.format && !entry.formats.includes(g.format)) entry.formats.push(g.format);
    (g.professions || []).forEach((p) => {
      if (p && !entry.professions.includes(p)) entry.professions.push(p);
    });

    if (g.average !== null && g.average !== undefined && g.responses > 0) {
      entry._scoreSum += g.average * g.responses;
      entry._scoreWeight += g.responses;
    }
  });

  return [...map.values()].map(({ _scoreSum, _scoreWeight, ...entry }) => ({
    ...entry,
    average: _scoreWeight > 0 ? Math.round((_scoreSum / _scoreWeight) * 10) / 10 : null
  }));
};

/** Palette de score partagée par les cartes, le tableau et le tiroir. */
export const scoreColor = (percent) => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) return grey(400);
  if (percent >= 90) return getTone('emerald').base;
  if (percent >= 75) return getTone('indigo').base;
  if (percent >= 50) return getTone('amber').base;
  return getTone('rose').base;
};

/** Conversion du pourcentage en note sur 5 (100 % = 5). */
export const noteOn5 = (percent) => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) return null;
  return Math.round((percent / 20) * 10) / 10;
};

export const scoreLabel = (percent) => {
  if (percent === null || percent === undefined || Number.isNaN(percent)) return 'Non noté';
  if (percent >= 90) return 'Excellent';
  if (percent >= 75) return 'Satisfaisant';
  if (percent >= 50) return 'À surveiller';
  return 'Critique';
};

export const FORMAT_STYLES = {
  'Présentiel': { color: getTone('sky').base, bg: toneAlpha('sky', 0.12) },
  'Classe virtuelle': { color: getTone('violet').base, bg: toneAlpha('violet', 0.12) },
  'E-learning': { color: getTone('teal').base, bg: toneAlpha('teal', 0.12) },
  'Mixte': { color: getTone('orange').base, bg: toneAlpha('orange', 0.12) }
};

export const formatStyle = (format) =>
  FORMAT_STYLES[format] || { color: getTone('neutral').base, bg: toneAlpha('neutral', 0.12) };

export const SESSION_STATUS_STYLES = {
  Active: { color: getTone('sky').base, bg: toneAlpha('sky', 0.12) },
  Fini: { color: getTone('emerald').base, bg: toneAlpha('emerald', 0.12) },
  Annulée: { color: getTone('rose').base, bg: toneAlpha('rose', 0.12) },
  Offerte: { color: getTone('violet').base, bg: toneAlpha('violet', 0.12) }
};

export const sessionStatusStyle = (status) =>
  SESSION_STATUS_STYLES[status] || { color: getTone('neutral').base, bg: toneAlpha('neutral', 0.12) };

/** « 12 – 14 mars 2026 », « 3 mars 2026 » ou « — ». */
export const formatDateRange = (start, end) => {
  const s = start ? moment(start) : null;
  const e = end ? moment(end) : null;
  if (!s || !s.isValid()) return '—';
  if (!e || !e.isValid() || s.isSame(e, 'day')) return s.format('D MMM YYYY');
  if (s.isSame(e, 'month')) return `${s.format('D')} – ${e.format('D MMM YYYY')}`;
  if (s.isSame(e, 'year')) return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;
  return `${s.format('D MMM YYYY')} – ${e.format('D MMM YYYY')}`;
};

/** Clé stable d'un groupe de la vue d'ensemble. */
export const groupKey = (group) => `${group.formation_id}-${group.session_id}`;

/**
 * Les questions à choix multiples sont stockées en tableau JSON *encodé en
 * chaîne* (« ["Ostéoporose","Infertilité"] »). Sans ce déballage, la cellule
 * affiche le JSON brut, crochets et guillemets compris.
 */
const unwrapAnswer = (answer) => {
  if (typeof answer !== 'string') return answer;
  const str = answer.trim();
  if (!str.startsWith('[') && !str.startsWith('{')) return str;
  try {
    const parsed = JSON.parse(str);
    return parsed !== null && typeof parsed === 'object' ? parsed : str;
  } catch (e) {
    return str;
  }
};

/**
 * Décompose une réponse en valeurs unitaires : une case cochée = une valeur.
 * C'est ce qui permet de compter chaque option séparément dans la synthèse.
 */
export const answerValues = (answer) => {
  if (answer === null || answer === undefined) return [];
  const value = unwrapAnswer(answer);
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) return value.flatMap(answerValues);
  if (typeof value === 'object') return [value.text || value.label || JSON.stringify(value)];
  const str = String(value).trim();
  return str === '' ? [] : [str];
};

/** Rendu d'une réponse pour une cellule de tableau. */
export const renderAnswer = (answer) => {
  const values = answerValues(answer);
  return values.length ? values.join(' · ') : '—';
};

/** Colonnes d'identité : déjà présentes dans la colonne « Apprenant ». */
const IDENTITY_LABELS = ['nom', 'prénom', 'prenom', 'nom et prénom', 'nom et prenom', 'nom & prénom'];

export const isIdentityQuestion = (text) => {
  const lower = String(text || '').toLowerCase().trim();
  const bare = lower.endsWith(':') ? lower.slice(0, -1).trim() : lower;
  return IDENTITY_LABELS.some((label) => bare === label || lower.startsWith(`${label} `));
};
